'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { projectPhoto } from '@/lib/projectPhoto';
import { NextVisuals, PhotoPrompt } from '@/components/ui/NextVisual';
import { projectTeaser, compareHref, beforeAfterHref } from '@/data/journey';
import { REVIEWS_SECTION } from '@/data/homepage';
import { IH_RENDERS } from '@/data/independentHomes';
import SitePhoto from '@/components/ui/SitePhoto';
import { SITE_PHOTOS } from '@/data/sitePhotos';
import ScrollReveal from '@/app/components/ScrollReveal';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import CTAPrimary from '@/components/ui/CTAPrimary';
import CTASecondary from '@/components/ui/CTASecondary';
import { COMPANY } from '@/data/company';

// Real photos from our finished Bangalore projects (each within 12% of its slot's shape — no crop)
const HERO_PHOTO = projectPhoto('gubbi-gudu-villa-anjanapura', 'parents-bedroom-tv-unit-and-wall-panels.jpg');
const MOBILE_PHOTO = projectPhoto('the-green-storey-g3-house-bannerghatta', 'dark-blue-l-shaped-modular-kitchen-with-white-lofts.jpg');
const GUBBI = projectTeaser('gubbi-gudu-villa-anjanapura');
// Reviews from clients who managed their project from another city — Kerala and Mumbai
const AWAY_REVIEWS = REVIEWS_SECTION.reviews.filter((r) => /Kerala|Mumbai/.test(r.projectLocation ?? ''));
const CTA_PHOTO = projectPhoto('blue-on-mars-apartment-banashankari', 'wooden-tv-wall-panel-with-lit-display-niche.jpg');

// ─── PROCESS STEPS ────────────────────────────────────────────────────────────

const PROCESS_STEPS = [
  { label: 'UNDERSTAND', note: 'Project scope, floor plan, requirements.' },
  { label: 'DESIGN', note: 'Spatial planning, material direction, documentation.' },
  { label: 'MATERIAL SELECTION', note: 'Options shortlisted for you, reviewed and confirmed.' },
  { label: 'DOCUMENTATION', note: 'Drawings, BOQ and manufacturing specs.' },
  { label: 'MANUFACTURE', note: 'Modular components produced in our facility.' },
  { label: 'INSTALLATION', note: 'Supervised on-site execution.' },
  { label: 'QC', note: 'Finish, fit and function verified.' },
  { label: 'HANDOVER', note: 'Project complete. Keys handed over.' },
];

// ─── WHAT WILLOW HANDLES ──────────────────────────────────────────────────────

const HANDLES = [
  { label: 'Interior Design', note: null },
  { label: 'Modular Furniture', note: 'Kitchens, wardrobes, TV units, beds, vanities and more.' },
  { label: 'False Ceiling', note: null },
  { label: 'Electrical', note: null },
  { label: 'Plumbing', note: null },
  { label: 'Procurement', note: null },
  { label: 'Installation', note: null },
  { label: 'Quality Checks', note: null },
  { label: 'Handover', note: null },
  { label: 'Civil Coordination', note: 'Willow coordinates civil work but does not directly execute civil construction.' },
];

// ─── WHY WILLOW ───────────────────────────────────────────────────────────────

const WHY_ITEMS = [
  {
    heading: 'Own Manufacturing',
    body: 'Willow manufactures its own modular products. Design, production and installation move through one coordinated system.',
  },
  {
    heading: 'Real Execution Experience',
    body: 'Our process comes from executing real interior projects — not from a manual. We know where things go wrong and how to prevent them.',
  },
  {
    heading: 'Documented Processes',
    body: 'Every stage is documented. Material selections, drawings, manufacturing specs and QC records are part of how we work.',
  },
  {
    heading: 'One Coordinated Journey',
    body: 'Design, manufacturing and installation are not three separate vendors. They are one coordinated interior journey.',
  },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function NRIInteriorsContent() {
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <div
      className="bg-warm-white min-h-screen"
      style={{ fontFamily: 'var(--font-sans)' }}
    >
      {/* ── BREADCRUMBS ─────────────────────────────────────────── */}
      <div className="container-willow pt-8 pb-0">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'NRI Interiors Bangalore' },
          ]}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — HERO
          Warm, calm, image-led. Not a generic real-estate hero.
          Vertical split: left = tall image, right = editorial text
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="nri-h1" className="overflow-hidden">
        <div className="container-willow">
          <div className="grid lg:grid-cols-[5fr_7fr] gap-0 min-h-[88vh] items-stretch">

            {/* Left — tall image, flush top */}
            <div className="relative hidden lg:block">
              <div className="sticky top-24 pt-10">
                <SitePhoto photo={HERO_PHOTO} aspectRatio="3/4" priority sizes="(min-width: 1024px) 40vw, 100vw" />
                <PhotoPrompt href={HERO_PHOTO.projectHref} className="mt-3">
                  Nikhil Kulkarni's {HERO_PHOTO.projectName} villa — see the full project
                </PhotoPrompt>
                {/* Subtle location tag */}
                <div
                  className="absolute bottom-8 left-8"
                  aria-hidden="true"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      width: '1.5rem',
                      height: '1px',
                      backgroundColor: '#AA7D53',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.22em',
                      textTransform: 'uppercase',
                      color: 'rgba(250,248,245,0.6)',
                    }}
                  >
                    BANGALORE, INDIA
                  </span>
                </div>
              </div>
            </div>

            {/* Right — editorial text block */}
            <div className="flex flex-col justify-center py-20 lg:py-28 lg:pl-16 xl:pl-24">

              {/* H1 — topic line (how NRIs search) + warm editorial slogan, in one heading */}
              <h1
                id="nri-h1"
                style={{
                  fontSize: 'clamp(2.6rem, 5.5vw, 5rem)',
                  fontWeight: 800,
                  lineHeight: 1.0,
                  letterSpacing: '-0.04em',
                  color: 'var(--deep)',
                  marginBottom: '2rem',
                }}
              >
                <span className="flex items-center gap-3 mb-10 leading-normal">
                  <span style={{ display: 'block', width: '2rem', height: '1px', backgroundColor: '#AA7D53' }} aria-hidden="true" />
                  <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--primary)' }}>
                    Home Interiors in Bangalore for NRIs
                  </span>
                </span>
                Your Bangalore Home,<br />
                <span style={{ color: 'var(--primary)' }}>Built While You're Away.</span>
              </h1>

              <p
                style={{
                  fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
                  lineHeight: 1.75,
                  color: 'var(--muted-foreground)',
                  maxWidth: '30rem',
                  marginBottom: '1.5rem',
                }}
              >
                Living in another city or country? Willow coordinates design, manufacturing and installation for your Bangalore home — one managed journey, so you don't have to coordinate vendors from afar.
              </p>

              {/* Core message — typographic pull */}
              <div
                style={{
                  borderLeft: '2px solid #AA7D53',
                  paddingLeft: '1.25rem',
                  marginBottom: '2.5rem',
                  maxWidth: '28rem',
                }}
              >
                <p
                  style={{
                    fontSize: 'clamp(0.85rem, 1.2vw, 0.95rem)',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--deep)',
                    lineHeight: 1.6,
                  }}
                >
                  Own a home in Bangalore.<br />
                  Live elsewhere.<br />
                  We can help build it.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <CTAPrimary label="Start Your Project" href="/contact" />
                <CTASecondary label="View Our Work" href="/projects" />
              </div>

              {/* Mobile image */}
              <div className="mt-12 lg:hidden">
                <SitePhoto photo={MOBILE_PHOTO} aspectRatio="4/3" sizes="100vw" />
                <PhotoPrompt href={MOBILE_PHOTO.projectHref} className="mt-3">
                  {MOBILE_PHOTO.projectName} — see the full project
                </PhotoPrompt>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 2 — THE CHALLENGE
          Warm stone background. Honest, calm explanation.
          Not a fear-based list — a considered acknowledgement.
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="nri-challenge"
        style={{ backgroundColor: 'var(--stone)' }}
        className="py-20 lg:py-28"
      >
        <div className="container-willow">
          <ScrollReveal>
            <div className="grid lg:grid-cols-[1fr_1fr] gap-16 lg:gap-24 items-center">

              {/* Left — editorial text */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span
                    style={{
                      display: 'block',
                      width: '1.5rem',
                      height: '1px',
                      backgroundColor: '#AA7D53',
                    }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    THE CHALLENGE
                  </span>
                </div>

                <h2
                  id="nri-challenge"
                  style={{
                    fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: '-0.03em',
                    color: 'var(--deep)',
                    marginBottom: '1.5rem',
                  }}
                >
                  Managing an interior project from another city — or another country — is genuinely difficult.
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 1.4vw, 1.05rem)',
                    lineHeight: 1.8,
                    color: 'var(--muted-foreground)',
                    marginBottom: '1.25rem',
                  }}
                >
                  Most interior projects involve multiple vendors: a designer, a carpenter, an electrician, a plumber, a procurement team. Each makes decisions independently. Coordination falls on the homeowner.
                </p>

                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 1.4vw, 1.05rem)',
                    lineHeight: 1.8,
                    color: 'var(--muted-foreground)',
                  }}
                >
                  When you are not physically present in Bangalore, that coordination becomes harder. Decisions get delayed. Execution drifts. The project stretches.
                </p>

                {/* Proof right where the worry is raised: clients who were not in Bangalore */}
                <div className="mt-8 flex flex-col gap-5">
                  <p className="text-section-label">Clients who weren't in Bangalore</p>
                  {AWAY_REVIEWS.map((r) => (
                    <blockquote key={r.clientName} className="border-l-2 pl-4" style={{ borderColor: 'var(--primary)' }}>
                      <p className="font-semibold text-foreground">{r.highlight}</p>
                      <footer className="mt-1 text-caption">{r.clientName} · {r.projectLocation}</footer>
                    </blockquote>
                  ))}
                  <PhotoPrompt href="/#reviews">Read what all our clients say</PhotoPrompt>
                </div>
              </div>

              {/* Right — image */}
              <div>
                <SitePhoto photo={SITE_PHOTOS.laminateArch} aspectRatio="4/5" className="w-full" />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 3 — ONE COORDINATED PROCESS
          Dark background. Process as a visual rhythm.
          Not a card grid — a flowing editorial sequence.
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="nri-process"
        style={{ backgroundColor: 'var(--deep)' }}
        className="py-20 lg:py-28"
      >
        <div className="container-willow">
          <ScrollReveal>
            <div className="mb-14">
              <div className="flex items-center gap-3 mb-6">
                <span
                  style={{
                    display: 'block',
                    width: '1.5rem',
                    height: '1px',
                    backgroundColor: '#AA7D53',
                  }}
                  aria-hidden="true"
                />
                <span
                  style={{
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.25em',
                    textTransform: 'uppercase',
                    color: 'var(--primary)',
                  }}
                >
                  ONE COORDINATED PROCESS
                </span>
              </div>
              <h2
                id="nri-process"
                style={{
                  fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  color: '#FAF8F5',
                  maxWidth: '36rem',
                }}
              >
                Design, manufacturing and installation — brought into one managed sequence.
              </h2>
            </div>
          </ScrollReveal>

          {/* Process — horizontal scroll on mobile, full grid on desktop */}
          <div className="overflow-x-auto pb-4 lg:pb-0">
            <div
              className="flex lg:grid gap-0"
              style={{
                gridTemplateColumns: `repeat(${PROCESS_STEPS?.length}, 1fr)`,
                minWidth: `${PROCESS_STEPS?.length * 140}px`,
              }}
            >
              {PROCESS_STEPS?.map((step, i) => (
                <button
                  key={step?.label}
                  onClick={() => setActiveStep(activeStep === i ? null : i)}
                  className="group text-left focus:outline-none"
                  aria-expanded={activeStep === i}
                  style={{
                    borderTop: `1px solid ${activeStep === i ? '#AA7D53' : 'rgba(250,248,245,0.12)'}`,
                    borderRight: 'none',
                    borderBottom: 'none',
                    borderLeft: 'none',
                    padding: '1.5rem 1rem',
                    transition: 'border-color 0.2s ease',
                    cursor: 'pointer',
                    background: 'transparent',
                  }}
                >
                  {/* Step number */}
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      letterSpacing: '0.2em',
                      color: activeStep === i ? 'var(--primary)' : 'rgba(250,248,245,0.3)',
                      marginBottom: '0.75rem',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {String(i + 1)?.padStart(2, '0')}
                  </span>

                  {/* Step label */}
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'clamp(0.7rem, 1vw, 0.8rem)',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: activeStep === i ? '#FAF8F5' : 'rgba(250,248,245,0.55)',
                      lineHeight: 1.3,
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {step?.label}
                  </span>

                  {/* Expanded note */}
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.75rem',
                      lineHeight: 1.6,
                      color: 'rgba(250,248,245,0.45)',
                      marginTop: '0.75rem',
                      maxHeight: activeStep === i ? '4rem' : '0',
                      overflow: 'hidden',
                      transition: 'max-height 0.3s ease',
                    }}
                  >
                    {step?.note}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Process note */}
          <ScrollReveal>
            <p
              style={{
                fontSize: '0.8rem',
                color: 'rgba(250,248,245,0.35)',
                marginTop: '2rem',
                letterSpacing: '0.04em',
              }}
            >
              Tap any stage to expand. ·{' '}
              <span style={{ fontStyle: 'italic' }}>
                Each stage is documented and coordinated — not handed off to a separate vendor.
              </span>
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 4 — WHAT WILLOW HANDLES
          Warm white. Large typographic list — editorial, not cards.
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="nri-handles"
        className="py-20 lg:py-28"
        style={{ backgroundColor: 'var(--warm-white)' }}
      >
        <div className="container-willow">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 lg:gap-24">

            {/* Left — heading + image */}
            <ScrollReveal>
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span
                    style={{
                      display: 'block',
                      width: '1.5rem',
                      height: '1px',
                      backgroundColor: '#AA7D53',
                    }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    WHAT WILLOW HANDLES
                  </span>
                </div>

                <h2
                  id="nri-handles"
                  style={{
                    fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)',
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: '-0.03em',
                    color: 'var(--deep)',
                    marginBottom: '1.5rem',
                  }}
                >
                  The full scope of your interior — coordinated through one team.
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(0.9rem, 1.3vw, 1rem)',
                    lineHeight: 1.8,
                    color: 'var(--muted-foreground)',
                    marginBottom: '2rem',
                  }}
                >
                  From the first design conversation to the final quality check, Willow manages the interior journey. You don't need to coordinate between vendors — we do that.
                </p>

                <SitePhoto photo={SITE_PHOTOS.sitePanels} companions={[SITE_PHOTOS.packedLoading]} aspectRatio="4/3" className="w-full" />
              </div>
            </ScrollReveal>

            {/* Right — scope list */}
            <ScrollReveal>
              <div>
                <ul
                  style={{ listStyle: 'none', padding: 0, margin: 0 }}
                  aria-label="What Willow handles"
                >
                  {HANDLES?.map((item, i) => (
                    <li
                      key={item?.label}
                      style={{
                        borderBottom: '1px solid var(--border)',
                        padding: '1.1rem 0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: '1rem',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.55rem',
                            fontWeight: 700,
                            letterSpacing: '0.15em',
                            color: 'var(--primary)',
                            minWidth: '1.5rem',
                          }}
                        >
                          {String(i + 1)?.padStart(2, '0')}
                        </span>
                        <span
                          style={{
                            fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            color: 'var(--deep)',
                          }}
                        >
                          {item?.label}
                        </span>
                      </div>
                      {item?.note && (
                        <p
                          style={{
                            fontSize: '0.8rem',
                            lineHeight: 1.6,
                            color: 'var(--muted-foreground)',
                            paddingLeft: '2.5rem',
                            fontStyle: 'italic',
                          }}
                        >
                          {item?.note}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 5 — WHY WILLOW
          Dark background. Four reasons — editorial, not a feature grid.
          Large image on one side.
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="nri-why"
        style={{ backgroundColor: 'var(--deep)' }}
        className="py-20 lg:py-28"
      >
        <div className="container-willow">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 lg:gap-24 items-start">

            {/* Left — image + eyebrow */}
            <ScrollReveal>
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span
                    style={{
                      display: 'block',
                      width: '1.5rem',
                      height: '1px',
                      backgroundColor: '#AA7D53',
                    }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    WHY WILLOW
                  </span>
                </div>

                <h2
                  id="nri-why"
                  style={{
                    fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)',
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: '-0.03em',
                    color: '#FAF8F5',
                    marginBottom: '2rem',
                  }}
                >
                  Design, manufacturing and installation — not three vendors. One coordinated interior journey.
                </h2>

                <SitePhoto photo={SITE_PHOTOS.floorDisplayArea} aspectRatio="4/3" className="w-full" />
              </div>
            </ScrollReveal>

            {/* Right — four reasons */}
            <ScrollReveal>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  paddingTop: '4rem',
                }}
              >
                {WHY_ITEMS?.map((item, i) => (
                  <div
                    key={item?.heading}
                    style={{
                      borderTop: '1px solid rgba(250,248,245,0.1)',
                      padding: '1.75rem 0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '1.25rem',
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '0.55rem',
                          fontWeight: 700,
                          letterSpacing: '0.15em',
                          color: 'var(--primary)',
                          minWidth: '1.5rem',
                          paddingTop: '0.2rem',
                        }}
                      >
                        {String(i + 1)?.padStart(2, '0')}
                      </span>
                      <div>
                        <h3
                          style={{
                            fontSize: 'clamp(0.95rem, 1.4vw, 1.05rem)',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: '#FAF8F5',
                            marginBottom: '0.5rem',
                          }}
                        >
                          {item?.heading}
                        </h3>
                        <p
                          style={{
                            fontSize: '0.9rem',
                            lineHeight: 1.75,
                            color: 'rgba(250,248,245,0.55)',
                          }}
                        >
                          {item?.body}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Link to manufacturing */}
                <div
                  style={{
                    borderTop: '1px solid rgba(250,248,245,0.1)',
                    paddingTop: '1.75rem',
                  }}
                >
                  <Link
                    href="/manufacturing"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                      textDecoration: 'none',
                      transition: 'opacity 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  >
                    Explore Manufacturing
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 7H11.5M8 3.5L11.5 7L8 10.5"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <NextVisuals
        lead="See it before you decide"
        tone="stone"
        items={[
          {
            kicker: '3D VS ACTUAL',
            title: `How close is our 3D to the finished home? See ${GUBBI.name}`,
            text: 'Every home is designed in 3D first — useful when you are approving from abroad.',
            href: compareHref(GUBBI.slug),
            ...(GUBBI.compare && { pair: { first: GUBBI.compare.render, second: GUBBI.compare.actual, firstLabel: '3D design', secondLabel: 'Finished' } }),
          },
          {
            kicker: 'INDEPENDENT HOUSES',
            title: 'Building a house or villa? See our 3D designs for independent homes',
            href: '/independent-house-interiors-bangalore#designs',
            image: IH_RENDERS.hero,
          },
        ]}
      />

      {/* ══════════════════════════════════════════════════════════
          SECTION 6 — CTA
          Warm, calm, inviting. Not a hard-sell banner.
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="nri-cta"
        style={{ backgroundColor: 'var(--stone)' }}
        className="py-20 lg:py-28"
      >
        <div className="container-willow">
          <ScrollReveal>
            <div className="grid lg:grid-cols-[1fr_1fr] gap-16 lg:gap-24 items-center">

              {/* Left — text */}
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <span
                    style={{
                      display: 'block',
                      width: '1.5rem',
                      height: '1px',
                      backgroundColor: '#AA7D53',
                    }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    START YOUR PROJECT
                  </span>
                </div>

                <h2
                  id="nri-cta"
                  style={{
                    fontSize: 'clamp(1.8rem, 3.5vw, 3rem)',
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                    color: 'var(--deep)',
                    marginBottom: '1.25rem',
                  }}
                >
                  Planning a Bangalore Home From Elsewhere?
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 1.4vw, 1.05rem)',
                    lineHeight: 1.8,
                    color: 'var(--muted-foreground)',
                    maxWidth: '28rem',
                    marginBottom: '2.5rem',
                  }}
                >
                  Tell us what you're planning. We'll start by understanding the project — the space, the scope and what you're looking to achieve.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <CTAPrimary label="Start Your Project" href="/contact" />
                  <a
                    href={COMPANY?.whatsapp?.message}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      color: 'var(--deep)',
                      border: '1px solid var(--deep)',
                      textDecoration: 'none',
                      transition: 'background-color 0.15s ease, color 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--deep)';
                      e.currentTarget.style.color = '#FAF8F5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--deep)';
                    }}
                  >
                    {/* WhatsApp icon */}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp Willow
                  </a>
                </div>
              </div>

              {/* Right — image */}
              <div>
                <SitePhoto photo={CTA_PHOTO} aspectRatio="4/5" sizes="(min-width: 1024px) 40vw, 100vw" />
                <PhotoPrompt href={beforeAfterHref('blue-on-mars-apartment-banashankari')} className="mt-3">
                  Siva &amp; Sonal's {CTA_PHOTO.projectName} — see the before and after
                </PhotoPrompt>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
